import esriConfig from '@arcgis/core/config';
import Basemap from '@arcgis/core/Basemap';
import Map from '@arcgis/core/Map';
import SceneView from '@arcgis/core/views/SceneView';
import MapView from '@arcgis/core/views/MapView';
import Search from '@arcgis/core/widgets/Search';
import Legend from '@arcgis/core/widgets/Legend';
import Expand from '@arcgis/core/widgets/Expand';
import ScaleBar from '@arcgis/core/widgets/ScaleBar';
import LayerList from '@arcgis/core/widgets/LayerList';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import LocalBasemapsSource from '@arcgis/core/widgets/BasemapGallery/support/LocalBasemapsSource';
import TileLayer from '@arcgis/core/layers/TileLayer';

export function bootstrapJSAPI(portalUrl, jsapiUrl, use3D) {
  return new Promise((resolve, reject) => {
    esriConfig.portalUrl = portalUrl;
    initApi(portalUrl).then(
      () => resolve(),
      error => reject(error)
    );
  });
}

export function createView(node, basemapId, use3D, viewOptions) {
  return new Promise((resolve, reject) => {
    initView(node, basemapId, use3D, viewOptions).then(
      response => {
        postInitView(response.view);
        resolve(response);
      },
      error => {
        reject(error);
      }
    );
  });
}

export function createSearch(view, node, searchOptions) {
  return new Promise((resolve, reject) => {
    let options = Object.assign({}, {
      view: view,
      container: document.createElement("searchDiv"),
      includeDefaultSources: false
    }, searchOptions);

    let search = new Search(options);
    view.ui.add(search, {
      position: "top-right"
    });

    view.ui.add("measurementTopbar", "top-right");

    resolve(search);
  });
}

export function createLegend(view, originLayer, viirsLayer) {
  return new Promise((resolve, reject) => {
    let incidentsLegend = new Legend({
      container: document.createElement("div"),
      view: view,
      layerInfos: [{
        layer: originLayer,
        title: "Incidents"
      },
      {
        layer: viirsLayer,
        title: "VIIRS"
      }]
    });

    var scaleBar = new ScaleBar({
      view: view,
      unit: "dual"
    });

    view.ui.add(scaleBar, {
      position: "bottom-right"
    });

    const legendExpand = new Expand({
      expandIconClass: "esri-icon-layer-list",
      expandTooltip: "Show Legend",
      view: view,
      content: incidentsLegend.domNode
    });
    view.ui.add(legendExpand, "bottom-left");

    incidentsLegend.style = "card";
    incidentsLegend.layout = "side-by-side";
    resolve(incidentsLegend);
  });
}

export function createLayerList(view, portalItemIds, addLayerFunction, removeLayerFunction) {
  return new Promise((resolve, reject) => {
    let layerList = new LayerList({
      container: document.createElement("div"),
      view: view,
      listItemCreatedFunction: function (event) {
        if (event.item.parent !== null && event.item.parent.title === 'User Layers') {
          event.item.actionsSections = [
            [
              {
                title: "Remove Layer",
                className: "esri-icon-trash",
                id: "remove-user-layer"
              }
            ]
          ];
        } else if (event.item.title !== "User Layers") {
          return false;
        } else {
          event.item.actionsSections = [
            [
              {
                title: "Add Layer",
                className: "esri-icon-settings2",
                id: "add-user-layer"
              }
            ]
          ];
        }
      }
    });

    layerList.on("trigger-action", (event) => {
      if (event.action.id === "add-user-layer") {
        addLayerFunction();
        return;
      }

      if (event.action.id === "remove-user-layer") {
        removeLayerFunction(event);
        return;
      }
    });

    const layerListExpand = new Expand({
      expandIconClass: "esri-icon-layers",
      expandTooltip: "Show Layer List",
      view: view,
      content: layerList.domNode
    });

    view.ui.add(layerListExpand, "top-left");

    view.ui.add("pauseTopbar", "top-left");

    resolve(layerList);
  });
}

function initApi(portalUrl) {
  return Promise.resolve();
}

function initView(node, basemapId, use3D, viewOptions) {
  return new Promise((resolve, reject) => {
    const basemap = (!basemapId) ? 'topo' : new Basemap({
      portalItem: {
        id: basemapId
      }
    });
    const map = new Map({
      basemap: basemap
    });

    const viewConfig = Object.assign({}, {
        map: map,
        container: node
      },
      viewOptions
    );

    if (use3D && supportsWebGL()) {
      const sceneView = new SceneView(viewConfig);
      sceneView.when(
        response => {
          resolve({
            view: response
          });
        },
        error => {
          const el = document.getElementById(node);
          if (el) el.innerHTML = '';
          let view = new MapView(viewConfig);

          view.when(
            response => {
              resolve({
                view: response
              });
            },
            error => {
              reject(error);
            }
          );
        }
      );
    } else {
      const view = new MapView(viewConfig);
      view.when(
        response => {
          resolve({
            view: response
          });
        },
        error => {
          reject(error);
        }
      );
    }
  });
}

function postInitView(view) {
  if (view.type === '2d' && supportsWebGL()) {
    view.map.basemap = 'topo-vector';
  }
  view.ui.remove("navigation-toggle");

  const basemapGallery = new BasemapGallery({
    view: view,
    container: document.getElementById("basemapGalleryDiv"),
    source: new LocalBasemapsSource({
      basemaps: [
        new Basemap({
          baseLayers: [new TileLayer({
            url: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer"
          })],
          title: "USGS Topo",
          id: "topo-basemap"
        }),
        Basemap.fromId("topo-vector"),
        Basemap.fromId("satellite")
      ]
    })
  });

  const bgExpand = new Expand({
    view: view,
    content: basemapGallery
  });

  view.ui.add(bgExpand, 'bottom-right');
}

function supportsWebGL() {
  var canvas = document.createElement("canvas");
  var gl = canvas.getContext("webgl") ||
    canvas.getContext("experimental-webgl");
  if (gl && gl instanceof WebGLRenderingContext) {
    return true;
  } else {
    return false;
  }
}
